import { getDefaultVenueImageBySport } from 'constants/venues';
import type { SportsEnum } from 'types/generated/client';
import Check from 'svg/Check';
import CloseIcon from 'svg/CloseIcon';
import Home from 'svg/Home';
import Outdoor from 'svg/Outdoor';
import Link from 'components/Link';
import classNames from 'styles/utils/classNames';

export interface LocationCardVenue {
  name: string;
  imageUrl?: string | null;
  isIndoor: boolean;
  /** Miles from the viewer; null/undefined when either has no coordinates. */
  distanceMiles?: number | null;
  primarySport?: SportsEnum | null;
}

interface Props {
  venue: LocationCardVenue;
  /** Selection ring + check circle (Figma "Location card / Selected"). */
  selected?: boolean;
  onToggle?: () => void;
  /** Shows a × circle instead of the check — for editable lists (onboarding, filters). */
  onRemove?: () => void;
  /** Makes the card a link to the venue. Ignored when `onToggle` is set. */
  href?: string;
  /** `fixed` (default) is the 140px design-system tile; `fluid` fills its grid cell. */
  width?: 'fixed' | 'fluid';
  className?: string;
}

/**
 * The 140px venue tile from the design system: cover, name, "Indoor • 5mi". Used by
 * the request-to-play court picker, the Find onboarding step and the home Filters sheet.
 */
export default function LocationCard({
  venue,
  selected = false,
  onToggle,
  onRemove,
  href,
  width = 'fixed',
  className,
}: Props) {
  const TypeIcon = venue.isIndoor ? Home : Outdoor;
  const distanceLabel =
    venue.distanceMiles != null ? ` • ${Math.round(venue.distanceMiles)}mi` : '';
  const wrapperClassName = classNames(
    'relative flex flex-col overflow-hidden rounded-lg bg-color-bg-lightmode-secondary text-left dark:bg-color-bg-darkmode-secondary',
    width === 'fixed' ? 'w-[8.75rem] shrink-0' : 'w-full',
    selected &&
      'shadow-[0px_0px_0px_2px_#fff,0px_0px_0px_3px_#4e4d54,0px_4px_20px_0px_rgba(0,0,0,0.16)]',
    className,
  );

  const body = (
    <>
      <div className="h-16 w-full bg-color-bg-lightmode-secondary dark:bg-color-bg-darkmode-secondary">
        <img
          src={venue.imageUrl || getDefaultVenueImageBySport(venue.primarySport)}
          alt=""
          className="h-full w-full object-cover"
        />
      </div>
      {onRemove ? (
        <span
          role="button"
          tabIndex={0}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRemove();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }
          }}
          aria-label={`Remove ${venue.name}`}
          className="absolute right-2 top-2 flex size-6 cursor-pointer items-center justify-center rounded-full bg-white shadow-[0px_4px_10px_0px_rgba(0,0,0,0.16)]"
        >
          <CloseIcon className="h-3 w-3 text-color-text-lightmode-primary" />
        </span>
      ) : (
        selected && (
          <span className="absolute right-2 top-2 flex size-6 items-center justify-center rounded-full bg-white shadow-[0px_4px_10px_0px_rgba(0,0,0,0.16)]">
            <Check className="h-3.5 w-3.5 text-color-text-lightmode-primary" />
          </span>
        )
      )}
      <div className="flex flex-col gap-1 p-ds-sm">
        <p className="typography-product-text-card-mobile line-clamp-2 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
          {venue.name}
        </p>
        <div className="flex items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          <TypeIcon className="h-3.5 w-3.5" />
          <span className="typography-product-text-card-mobile whitespace-nowrap">
            {venue.isIndoor ? 'Indoor' : 'Outdoor'}
            {distanceLabel}
          </span>
        </div>
      </div>
    </>
  );

  // A selectable card and a link card can't be the same element, so each shape
  // gets its own wrapper rather than one dynamic tag.
  if (onToggle) {
    return (
      <button type="button" onClick={onToggle} aria-pressed={selected} className={wrapperClassName}>
        {body}
      </button>
    );
  }

  if (href) {
    return (
      <Link href={href} className={wrapperClassName}>
        {body}
      </Link>
    );
  }

  return <div className={wrapperClassName}>{body}</div>;
}
